import { createSlice } from "@reduxjs/toolkit";
import { selectUser } from "../user/userSlice";
import { api } from "../../common/setupApi";
import {
  addAlert,
  addDefaulErrorAlert,
  handleApiError,
} from "../alerts/alertsSlice";
import { selectTxt } from "../lang/langSlice";
import { urlB64ToUint8Array } from "../../common/utils";

const alertDuration = 15000;

const unregisterFcmServiceWorker = async () => {
  try {
    const registrations = await navigator.serviceWorker.getRegistrations();

    for (const registration of registrations) {
      const scriptUrl =
        registration.active?.scriptURL ??
        registration.waiting?.scriptURL ??
        registration.installing?.scriptURL;

      if (scriptUrl?.endsWith("/firebase-messaging-sw.js")) {
        await registration.unregister();
        console.log("Unregistered firebase-messaging-sw.js");
      }
    }
  } catch (error) {
    console.error("ERROR during unregister FCM service worker", error);
  }
};

const getPushManager = async () => {
  let pushManager = window.pushManager; // DWP

  if (!pushManager) {
    const registrations = await navigator.serviceWorker.getRegistrations();

    for (const registration of registrations) {
      const scriptUrl =
        registration.active?.scriptURL ??
        registration.waiting?.scriptURL ??
        registration.installing?.scriptURL;

      if (scriptUrl?.endsWith("/service-worker.js")) {
        pushManager = registration.pushManager;
        break;
      }
    }
  }

  return pushManager;
};

const sub = ({ callback, silent = false } = {}) => {
  return async (dispatch, getState) => {
    const alertMsg = "Error during push notif subscription";
    const notAvailableMsg = selectTxt(getState(), "notifications")[26];

    const vapidPublicKey = selectVapidPublicKey(getState());
    if (!vapidPublicKey) {
      const msg = `${alertMsg}: vapid key not found`;
      console.error(msg);
      dispatch(addAlert(msg, "error", alertDuration));
      return;
    }

    try {
      dispatch(pushNotifActions.setSubscribing(true));

      await unregisterFcmServiceWorker();

      const pushManager = await getPushManager();

      if (!pushManager) {
        const msg = `${alertMsg}: pushManager not found`;
        console.error(msg);
        // dispatch(addAlert(msg, "error", alertDuration));
        dispatch(addAlert(notAvailableMsg, "error", alertDuration));
        return;
      }

      const existingSubscription = await pushManager.getSubscription();

      const subscription =
        existingSubscription ??
        (await pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlB64ToUint8Array(vapidPublicKey),
        }));

      if (!subscription) {
        const msg = `${alertMsg}: subscription not created`;
        console.error(msg);
        // dispatch(addAlert(msg, "error", alertDuration));
        dispatch(addAlert(notAvailableMsg, "error", alertDuration));
        return;
      }

      return api
        .post("notifications/subscribe-vapid/", { subscription })
        .then(() => {
          callback?.();
          if (!silent) {
            const sucessAlertMessage = selectTxt(
              getState(),
              "notifications"
            )[6];
            dispatch(addAlert(sucessAlertMessage, "success"));
          }
        })
        .catch((error) => {
          handleApiError(error);
        });
    } catch (error) {
      console.error(`${alertMsg}:`, error);
      dispatch(addDefaulErrorAlert());
    } finally {
      dispatch(pushNotifActions.setSubscribing(false));
    }
  };
};

export const subPushNotif = ({ callback } = {}) => {
  return async (dispatch, getState) => {
    const permission = await Notification.requestPermission();

    if (permission === "denied") {
      const message = selectTxt(getState(), "notifications")[4];
      dispatch(addAlert(message, "info", alertDuration));
    }

    if (permission === "granted") {
      dispatch(sub({ callback }));
    }
  };
};

// silent process - no alert messages
export const unsubPushNotif = () => {
  return async (dispatch) => {
    try {
      dispatch(pushNotifActions.setUnsubscribing(true));

      const pushManager = await getPushManager();

      if (!pushManager) {
        console.error("Error during unsub push notif: pushManager not found");
        return;
      }

      const subscription = await pushManager.getSubscription();

      if (!subscription) {
        console.error("Error during unsub push notif: subscription not found");
        return;
      }

      const endpoint = subscription.endpoint;

      await subscription.unsubscribe();

      return api
        .post("notifications/unsubscribe-vapid/", { endpoint })
        .catch((error) => {
          console.log(error?.response);
        });
    } catch (error) {
      console.error("Error during unsub push notif:", error);
    } finally {
      dispatch(pushNotifActions.setUnsubscribing(false));
    }
  };
};

export const checkPushNotifSub = () => {
  return async (dispatch) => {
    const permission = Notification.permission;

    if (permission === "granted") {
      dispatch(sub({ silent: true }));
    }
  };
};

const initialState = {
  subscribing: false,
  unsubscribing: false,
};

const pushNotifSlice = createSlice({
  name: "pushNotif",
  initialState,
  reducers: {
    setSubscribing: (state, action) => {
      state.subscribing = action.payload;
    },
    setUnsubscribing: (state, action) => {
      state.unsubscribing = action.payload;
    },
  },
});

export const pushNotifActions = pushNotifSlice.actions;

export const selectPushNotifSubscribing = (state) =>
  state.pushNotif.subscribing;

export const selectPushNotifUnsubscribing = (state) =>
  state.pushNotif.unsubscribing;

export const selectVapidPublicKey = (state) =>
  selectUser(state).data.vapid_public_key;

export default pushNotifSlice.reducer;

// const subDWP = ({ callback, silent = false } = {}) => {
//   return async (dispatch, getState) => {
//     dispatch(addAlert("subDWP", "info"));

//     const vapidPublicKey = selectVapidPublicKey(getState());
//     if (!vapidPublicKey) {
//       console.error("[VAPID KEY ERROR] No vapid key found");
//       // dispatch(addDefaulErrorAlert());
//       dispatch(addAlert("No Vapid key", "error"));
//       return;
//     }

//     try {
//       dispatch(pushNotifActions.setSubscribing(true));

//       const subscription = await window.pushManager.subscribe({
//         userVisibleOnly: true,
//         applicationServerKey: urlB64ToUint8Array(vapidPublicKey),
//       });

//       console.log("subscription", subscription);
//       if (!subscription) {
//         console.error("const subscription not created");
//         // dispatch(addDefaulErrorAlert());
//         dispatch(addAlert("subscription not created", "error"));
//         return;
//       }

//       await unregisterFcmServiceWorker();
//       dispatch(addAlert("unregisterFcmServiceWorker", "info"));

//       dispatch(addAlert(JSON.stringify(subscription), "info"));

//       return api
//         .post("notifications/subscribe-vapid/", { subscription })
//         .then(() => {
//           callback?.();
//           // if (!silent) {
//           const sucessAlertMessage = selectTxt(getState(), "notifications")[6];
//           dispatch(addAlert(sucessAlertMessage, "success"));
//           // }
//         })
//         .catch((error) => {
//           handleApiError(error);
//         });
//     } catch (error) {
//       console.error("Error in DWP subscription: ", error);
//       dispatch(addDefaulErrorAlert());
//     } finally {
//       dispatch(pushNotifActions.setSubscribing(false));
//     }
//   };
// };

// const subFCM = ({ callback, silent = false } = {}) => {
//   return async (dispatch, getState) => {
//     try {
//       dispatch(addAlert("subFCM", "info"));

//       dispatch(pushNotifActions.setSubscribing(true));

//       await registerFcmServiceWorker();
//       dispatch(addAlert("registerFcmServiceWorker", "info"));

//       const currentToken = await getFirebaseMessagingToken();

//       if (!currentToken) {
//         // dispatch(addDefaulErrorAlert());
//         console.error("[FCM ERROR] currentToken is null");
//         dispatch(addAlert("currentToken is null", "error"));
//         return;
//       }

//       dispatch(addAlert("currentToken:", currentToken, "info"));

//       return api
//         .post("notifications/subscribe/", { registration_id: currentToken })
//         .then(() => {
//           callback?.();
//           // if (!silent) {
//           const sucessAlertMessage = selectTxt(getState(), "notifications")[6];
//           dispatch(addAlert(sucessAlertMessage, "success"));
//           // }
//         })
//         .catch((error) => {
//           dispatch(handleApiError(error));
//         });
//     } catch (error) {
//       console.error("Error in FCM subscription: ", error);
//       dispatch(addDefaulErrorAlert());
//     } finally {
//       dispatch(pushNotifActions.setSubscribing(false));
//     }
//   };
// };

// const unsubFcm = ({ callback } = {}) => {
//   return async (dispatch) => {
//     if (!isNotificationSupported || Notification.permission !== "granted") {
//       return;
//     }

//     try {
//       const currentToken = await getFirebaseMessagingToken();

//       if (!currentToken) {
//         console.error("[FCM ERROR] currentToken is null");
//         return;
//       }

//       await unregisterFcmServiceWorker();

//       return api
//         .post("notifications/unsubscribe/", { registration_id: currentToken })
//         .catch((error) => {
//           console.error(error?.response);
//         });
//     } catch (error) {
//       console.error("Error un unsubFcm: ", error);
//     } finally {
//       callback?.();
//     }
//   };
// };
